import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useFilmDrop, type FilmDropRecord } from "../hooks/useFilmDrop";
import { formatDateTime, isoWeeksAgo, isoToday } from "../lib/format";

function formatNegReturn(value?: string): string {
  if (!value) return "";
  const lower = value.toLowerCase();
  if (lower.includes("no negs") || lower.includes("no negatives")) return "No Negs";
  if (lower.includes("like them cut") || (lower.includes("cut") && !lower.includes("uncut"))) return "Cut";
  if (lower.includes("uncut") || lower.includes("left uncut")) return "Uncut";
  if (lower.includes("mail") && (lower.includes("back") || lower.includes("return"))) return "Mailed";
  return value;
}
function truncate(text?: string, maxLength = 20): string {
  if (!text) return "";
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
}
const n = (v: unknown) => (typeof v === "number" ? v : Number(v) || 0);

export function FilmDropTab({ active, onOpenHistory }: { active: boolean; onOpenHistory: (id: string, name?: string) => void }) {
  const navigate = useNavigate();
  const [start, setStart] = useState(() => isoWeeksAgo(8));
  const [end, setEnd] = useState(() => isoToday());
  const [range, setRange] = useState<{ start: string; end: string } | null>({ start: isoWeeksAgo(8), end: isoToday() });
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data } = useFilmDrop(range, active);
  const records = data?.records ?? [];
  const summary = data?.summary ?? { today: 0, week: 0, month: 0 };

  const key = (r: FilmDropRecord) => String(r.rowid);
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleAll = (checked: boolean) => setSelected(checked ? new Set(records.map(key)) : new Set());
  const allChecked = records.length > 0 && selected.size === records.length;

  async function bulkDownload(formType: "prepaid" | "mailback" | "orderform") {
    const field = formType === "prepaid" ? "mail-in-pdf" : formType === "mailback" ? "mail-back-pdf" : "customer-form";
    const urls = records
      .filter((r) => selected.has(key(r)))
      .map((r) => r[field] as string | undefined)
      .filter((u): u is string => !!u);
    if (!urls.length) return;
    for (let i = 0; i < urls.length; i++) {
      const link = document.createElement("a");
      link.href = urls[i];
      link.download = "";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      if (i < urls.length - 1) await new Promise((res) => setTimeout(res, 300));
    }
  }

  const viewOrder = (r: FilmDropRecord) =>
    navigate({ to: "/orders/$invoiceId", params: { invoiceId: String(r["invoice-id"] || r.rowid) } });

  return (
    <div className="tab-content" style={{ display: active ? undefined : "none" }}>
      <div className="film-drop-summary">
        <div className="summary-card"><h3>Today</h3><div className="roll-count">{summary.today} rolls</div></div>
        <div className="summary-card"><h3>This Week</h3><div className="roll-count">{summary.week} rolls</div></div>
        <div className="summary-card"><h3>This Month</h3><div className="roll-count">{summary.month} rolls</div></div>
      </div>

      <div className="film-drop-controls">
        <input type="text" placeholder="Search by name..." />
        <label style={{ marginLeft: 10, color: "#666" }}>From:</label>
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        <label style={{ marginLeft: 5, color: "#666" }}>To:</label>
        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        <button onClick={() => setRange(start && end ? { start, end } : null)}>Apply</button>
        <button
          onClick={() => {
            const s = isoWeeksAgo(8);
            const e = isoToday();
            setStart(s);
            setEnd(e);
            setRange({ start: s, end: e });
          }}
        >
          Clear Filters
        </button>
        <button
          onClick={() => {
            setStart("");
            setEnd("");
            setRange(null);
          }}
          style={{ marginLeft: 10, backgroundColor: "#37358F" }}
        >
          Show All
        </button>
      </div>

      <div
        className="film-drop-bulk-actions"
        style={{
          display: selected.size > 0 ? "flex" : "none",
          padding: "10px 20px",
          background: "#f0f0f0",
          borderRadius: 6,
          margin: "0 20px 10px 20px",
          alignItems: "center",
          gap: 15,
        }}
      >
        <span style={{ fontWeight: 600, color: "#37358F" }}>{selected.size} selected</span>
        <button onClick={() => bulkDownload("prepaid")} style={{ background: "#2ecc71", padding: "6px 12px", fontSize: 13 }}>⬇ Prepaid Labels</button>
        <button onClick={() => bulkDownload("mailback")} style={{ background: "#3498db", padding: "6px 12px", fontSize: 13 }}>⬇ Mailback Labels</button>
        <button onClick={() => bulkDownload("orderform")} style={{ background: "#9b59b6", padding: "6px 12px", fontSize: 13 }}>⬇ Order Forms</button>
        <button onClick={() => setSelected(new Set())} style={{ background: "#95a5a6", padding: "6px 12px", fontSize: 13 }}>Clear Selection</button>
      </div>

      <div className="table-container">
        <table id="filmDropTable">
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <input type="checkbox" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} title="Select All" />
              </th>
              <th>Timestamp</th>
              <th style={{ width: 100 }}>Forms</th>
              <th style={{ width: 150 }}>Customer Name</th>
              <th style={{ width: 120 }}>Email</th>
              <th>Color 35</th>
              <th>B&amp;W 35</th>
              <th>Color 120</th>
              <th>B&amp;W 120</th>
              <th>Total Rolls</th>
              <th style={{ width: 100 }}>Scan Res</th>
              <th>File Format</th>
              <th style={{ width: 100 }}>Neg Return</th>
              <th style={{ width: 200 }}>Notes</th>
              <th>Neg Pickup</th>
              <th>Contact Sheet</th>
              <th>Scan Gallery</th>
              <th>Prints</th>
              <th>Join Club</th>
              <th>Club Member</th>
              <th style={{ width: 110 }}>Actions</th>
            </tr>
          </thead>
          <tbody id="filmDropTableBody">
            {records.map((record) => {
              const id = key(record);
              const totalRolls = n(record["color-35"]) + n(record["bw-35"]) + n(record["color-120"]) + n(record["bw-120"]);
              const isMailIn = record.order_type === "mail_in" || record.order_type === "mail_in-club";
              const cs = record["contact-sheet"];
              const sg = record["scan-gallery"];
              return (
                <tr key={id} className={isMailIn ? "mail-in-order" : ""}>
                  <td style={{ textAlign: "center" }}>
                    <input type="checkbox" className="film-drop-row-checkbox" checked={selected.has(id)} onChange={() => toggle(id)} />
                  </td>
                  <td>{formatDateTime(record.timestamp)}</td>
                  <td style={{ width: 100 }}>
                    {record["customer-form"] && (
                      <a href={record["customer-form"]} target="_blank" rel="noreferrer" style={{ color: "#9b59b6", textDecoration: "none", fontSize: 11 }}>order</a>
                    )}{" "}
                    {record["mail-back-pdf"] && (
                      <a href={record["mail-back-pdf"]} target="_blank" rel="noreferrer" style={{ color: "#3498db", textDecoration: "none", fontSize: 11 }}>mailback</a>
                    )}{" "}
                    {record["mail-in-pdf"] && (
                      <a href={record["mail-in-pdf"]} target="_blank" rel="noreferrer" style={{ color: "#2ecc71", textDecoration: "none", fontSize: 11 }}>prepaid</a>
                    )}
                  </td>
                  <td style={{ width: 150, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={record["full-name"] || ""}>
                    {record["club-member"] ? (
                      <span style={{ color: "#f1c40f", marginRight: 3 }} title="Club Member">★</span>
                    ) : null}
                    {truncate(record["full-name"], 20)}
                  </td>
                  <td style={{ width: 120, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={record.email || ""}>
                    {truncate(record.email, 18)}
                  </td>
                  <td>{n(record["color-35"])}</td>
                  <td>{n(record["bw-35"])}</td>
                  <td>{n(record["color-120"])}</td>
                  <td>{n(record["bw-120"])}</td>
                  <td><strong>{totalRolls}</strong></td>
                  <td style={{ width: 100, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={record["scan-resolution"] || ""}>
                    {truncate(record["scan-resolution"], 15)}
                  </td>
                  <td>{record["file-format"] || ""}</td>
                  <td style={{ width: 100 }} title={record["negative-return"] || ""}>{formatNegReturn(record["negative-return"])}</td>
                  <td style={{ width: 200, whiteSpace: "normal", wordWrap: "break-word" }}>{record.notes || ""}</td>
                  <td>{record["negative-pickup"] || ""}</td>
                  <td>{cs === 1 || cs === "1" ? "✓" : ""}</td>
                  <td>{sg === "A zip file via email is fine!" || sg === "I'd like a private scan gallery link" ? "✓" : ""}</td>
                  <td>{record.prints === "Yes please, for all my rolls" ? "✓" : ""}</td>
                  <td>{record["join-club"] === "Yes, I'd like to join!" ? "✓" : ""}</td>
                  <td>{record["club-member"] ? "✓" : ""}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <a href="#" className="view-link" onClick={(e) => { e.preventDefault(); viewOrder(record); }}>View</a>{" "}
                    <a
                      href="#"
                      className="view-link"
                      onClick={(e) => {
                        e.preventDefault();
                        onOpenHistory(String(record["invoice-id"] || record.rowid), record["full-name"] || undefined);
                      }}
                    >
                      History
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
